/* Atlas v3 — "Other Accounts": the manually-fed portfolio, extracted from the
   old My Stocks HOLDINGS sub-tab into the third stop of the My Stocks cluster
   (WATCHLIST / MY AGENT / ACCOUNTS — DeskSwitcher eyebrow, real routes).

   Demoted, not degraded: keeps everything the sub-tab had — consolidated
   positions from /api/portfolio joined with live prices, day P&L header,
   1D sparklines (matching the 1D % the row shows), and the screenshot→
   holdings import CTA (real UploadModal → Claude vision extract). Brings
   its own shell like every v3 screen. */
import { useState, useEffect, useMemo } from 'react'
import { useUser, useAuth } from '@clerk/clerk-react'
import { TickerCtx, intent, Spark } from './lib'
import { TickerStrip, BottomNav, SearchPill, DeskSwitcher, MY_STOCKS_DESKS } from './shell'
import { StockLogo } from '@/v2/components/StockLogo'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { useTickerBars } from '@/v2/hooks/useChartBars'
import { UploadModal } from '@/v2/components/portfolio/UploadModal'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }

/* ── price/% column (same shape as the watchlist rows') ── */
function PriceCol({ price, pct, valueLabel, minWidth = 62 }) {
  const up = (pct ?? 0) >= 0
  return (
    <div style={{ textAlign: 'right', flexShrink: 0, minWidth }}>
      <div style={{ ...Mono, fontSize: 13, color: 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums' }}>
        {valueLabel != null ? valueLabel : (price > 0 ? `$${price.toFixed(2)}` : '—')}
      </div>
      <div style={{ ...Mono, fontSize: 11, marginTop: 3, fontVariantNumeric: 'tabular-nums', color: pct != null ? `hsl(var(${intent(up)}))` : 'hsl(var(--studio-text-subtle))' }}>
        {pct != null ? `${up ? '+' : ''}${pct.toFixed(1)}%` : '—'}
      </div>
    </div>
  )
}

/* ── the holdings body — consolidated shares from /api/portfolio joined with
   live prices. Import CTA opens the real UploadModal (vision extract). ── */
function HoldingsPanel({ openTicker }) {
  const { user, isLoaded } = useUser()
  const { getToken } = useAuth()
  const userId = user?.id || null
  const [uploads, setUploads] = useState(null) // null = loading
  const [showUpload, setShowUpload] = useState(false)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    if (!isLoaded) return
    if (!userId) { setUploads([]); return }
    let cancelled = false
    ;(async () => {
      try {
        const token = await getToken()
        if (cancelled) return
        const r = await fetch('/api/portfolio?action=read', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
        const j = await r.json().catch(() => ({}))
        if (!cancelled) setUploads(r.ok && Array.isArray(j.uploads) ? j.uploads : [])
      } catch { if (!cancelled) setUploads([]) }
    })()
    return () => { cancelled = true }
  }, [userId, isLoaded, getToken, reload])

  const sharesByTicker = useMemo(() => {
    const shares = {}
    for (const u of (uploads || [])) {
      for (const h of (u.holdings || [])) {
        shares[h.ticker] = (shares[h.ticker] || 0) + (Number(h.shares) || 0)
      }
    }
    return shares
  }, [uploads])
  const { stocks } = useAtlasStocks()
  const nameByTicker = useMemo(() => new Map((stocks || []).map((s) => [s.ticker, s.name])), [stocks])
  const tickers = useMemo(() => Object.keys(sharesByTicker), [sharesByTicker])
  const { prices, loading: pricesLoading } = useLivePrices(tickers)
  // Real close series for the holding-row sparklines, matching the 1D %
  // this row displays (was hardcoded '5D', which never matched the shown %).
  const { barsByTicker } = useTickerBars(tickers, '1D')

  const rows = useMemo(() => {
    const rs = []
    for (const [t, shares] of Object.entries(sharesByTicker)) {
      const q = prices[t]
      if (!q?.price) continue
      const val = q.price * shares
      // change1d can be legitimately null from /api/prices (midquote,
      // prev-day fallback, delayed) — keep it null, never fabricate 0.
      rs.push({ ticker: t, name: nameByTicker.get(t) || t, shares, price: q.price, pct: q.change1d ?? null, val, spark: barsByTicker[t] ?? null })
    }
    return rs.sort((a, b) => b.val - a.val)
  }, [sharesByTicker, prices, nameByTicker, barsByTicker])

  const total = rows.reduce((a, r) => a + r.val, 0)
  // Day P/L over positions that actually have a change% — null when none do,
  // so a feed gap reads as "—" instead of a fabricated $0 day.
  const pricedRows = rows.filter((r) => r.pct != null)
  const pricedVal = pricedRows.reduce((a, r) => a + r.val, 0)
  const dayPL = pricedRows.length ? pricedRows.reduce((a, r) => a + r.val * (r.pct / 100), 0) : null
  const dayPct = dayPL != null && pricedVal ? (dayPL / pricedVal) * 100 : null
  const up = (dayPL ?? 0) >= 0

  // Hold the skeleton until prices settle too — otherwise the header paints a
  // "0 POSITIONS / $0 / +0.0%" flash while uploads are loaded but live prices
  // (and thus rows) haven't arrived yet.
  const loading = uploads === null || (tickers.length > 0 && pricesLoading && rows.length === 0)
  const empty = !loading && rows.length === 0

  return (
    <div style={{ paddingBottom: 24 }}>
      {!empty && (
        <div style={{ padding: '6px 20px 0' }}>
          <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))', textTransform: 'uppercase' }}>{rows.length} positions</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 4 }}>
            <span style={{ ...Display, fontSize: 32, lineHeight: 1, color: 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums' }}>
              ${total.toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </span>
            {dayPL != null && (
              <span style={{ ...Mono, fontSize: 13, color: `hsl(var(${intent(up)}))`, fontVariantNumeric: 'tabular-nums' }}>
                {up ? '+' : '−'}${Math.abs(dayPL).toLocaleString('en-US', { maximumFractionDigits: 0 })} · {up ? '+' : ''}{dayPct.toFixed(1)}%
              </span>
            )}
          </div>
          <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginTop: 5 }}>TODAY · MARKET VALUE</div>
        </div>
      )}

      {loading && (
        <div style={{ marginTop: 14 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderTop: '1px solid hsl(var(--studio-border) / 0.35)' }}>
              <div className="atlas-skeleton" style={{ width: 30, height: 30, borderRadius: '50%' }} />
              <div style={{ flex: 1 }}><div className="atlas-skeleton" style={{ width: '46%', height: 13, borderRadius: 3 }} /><div className="atlas-skeleton" style={{ width: '30%', height: 9, borderRadius: 3, marginTop: 7 }} /></div>
              <div className="atlas-skeleton" style={{ width: 56, height: 22, borderRadius: 4 }} />
            </div>
          ))}
        </div>
      )}

      {empty && (
        <div style={{ padding: '34px 20px 26px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ ...Display, fontSize: 24, color: 'hsl(var(--studio-text))', marginBottom: 9, lineHeight: 1.1 }}>No positions yet</div>
          <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-subtle))', lineHeight: 1.65, maxWidth: 240, marginBottom: 20 }}>
            Import your holdings from a brokerage screenshot — Atlas reads it once and discards the image.
          </div>
        </div>
      )}

      {!loading && (
        <div style={{ marginTop: empty ? 0 : 14 }}>
          {rows.map((r, i) => (
            <div key={r.ticker} onClick={() => openTicker(r.ticker)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', cursor: 'pointer',
                borderTop: i ? '1px solid hsl(var(--studio-border) / 0.35)' : '1px solid hsl(var(--studio-border) / 0.5)' }}>
              <StockLogo ticker={r.ticker} size={30} rounded={15} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ ...Mono, fontSize: 13.5, color: 'hsl(var(--studio-text))' }}>{r.ticker}</span>
                </div>
                <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>{r.shares} sh · ${r.price.toFixed(2)}</div>
              </div>
              <Spark data={r.spark} up={(r.pct ?? 0) >= 0} />
              <PriceCol price={r.price} pct={r.pct} valueLabel={`$${r.val.toLocaleString('en-US', { maximumFractionDigits: 0 })}`} minWidth={70} />
            </div>
          ))}

          {/* import — quiet utility row → real UploadModal */}
          <div onClick={() => setShowUpload(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 13, margin: '14px 0 0', padding: '14px 20px', cursor: 'pointer', borderTop: '1px solid hsl(var(--studio-border) / 0.5)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--studio-accent-soft))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" />
            </svg>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...Sans, fontSize: 13, color: 'hsl(var(--studio-text))' }}>{rows.length ? 'Update from a screenshot' : 'Import from a screenshot'}</div>
              <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-subtle))', marginTop: 3 }}>Atlas reads your brokerage positions</div>
            </div>
            <span style={{ color: 'hsl(var(--studio-accent-soft))', fontSize: 18, flexShrink: 0 }}>›</span>
          </div>
          <div style={{ padding: '16px 20px 6px', ...Mono, fontSize: 9, letterSpacing: '0.12em', color: 'hsl(var(--studio-text-subtle))', textAlign: 'center' }}>
            POSITIONS STAY ON YOUR ACCOUNT · NOT INVESTMENT ADVICE
          </div>
        </div>
      )}

      <UploadModal
        open={showUpload}
        userId={userId}
        onClose={() => setShowUpload(false)}
        onSaved={() => { setShowUpload(false); setReload((n) => n + 1) }}
      />
    </div>
  )
}

function AccountsBody() {
  const { openTicker } = useStockDetail()
  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ padding: '18px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ ...Display, fontSize: 30, color: 'hsl(var(--studio-text))', lineHeight: 1 }}>Other Accounts</div>
            <SearchPill />
          </div>
          <DeskSwitcher items={MY_STOCKS_DESKS} variant="tabs" />
        </div>
        <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', marginTop: 14, position: 'relative' }}>
          <HoldingsPanel openTicker={openTicker} />
        </div>
        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

export function Accounts() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <AccountsBody />
      </StockDetailProvider>
    </div>
  )
}
